import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { PencilIcon, PhotoIcon } from '@heroicons/react/20/solid'
import axios from 'axios'
import clsx from 'clsx'
import { UploadResponse } from 'imagekit-javascript/dist/src/interfaces'
import { IKUpload } from 'imagekitio-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSWRConfig } from 'swr'
import Button from '~/src/components/Button'
import FadeImage from '~/src/components/FadeImage'
import ModalDialog from '~/src/components/ModalDialog'
import TryAgain from '~/src/components/TryAgain'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import { useAppContext } from '~/src/context/AppProvider'
import type { ResultsResp } from '~/src/db/queryResultsResp'
import useGetParsedResults from '~/src/hooks/useGetParsedResults'
import useImageKit from '~/src/hooks/useImageKit'
import logger from '~/src/lib/logger'

type InputData = {
  title: string | null | undefined
  image: string | null | undefined
  price: string | number | null | undefined
  currency: string | null | undefined
  tags: string | null | undefined
}

interface Props {
  resultsData: ResultsResp
}

const EditProductModal: React.FC<Props> = ({ resultsData }) => {
  const { hash, authUser } = useAppContext()
  const imageKit = useImageKit()

  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const {
    data,
    refreshing,
    refresh: fetchResultsData,
    endpoint,
  } = useGetParsedResults({ hash: resultsData?.hash, skip: !isOpen })

  const { mutate } = useSWRConfig()

  const { register, handleSubmit, setValue, watch, reset } = useForm<InputData>(
    {
      defaultValues: {
        title: resultsData?.title,
        price: resultsData?.pricing?.price,
        currency: resultsData?.pricing?.currency || 'USD',
        image: resultsData?.image,
        tags: resultsData?.tags?.join(', '),
      },
    }
  )

  const image = watch('image') || resultsData?.image

  const processing = submitting || uploading

  const onSubmit = async (data: InputData) => {
    // if data hasn't changed, don't submit
    if (
      data.title === resultsData?.title &&
      data.price === resultsData?.pricing?.price &&
      data.currency === resultsData?.pricing?.currency &&
      data.image === resultsData?.image &&
      data.tags === resultsData?.tags?.join(', ')
    ) {
      return
    }

    setSubmitting(true)
    try {
      const response = await axios.post(
        '/api/edit-product',
        {
          hash,
          ...data,
        },
        {
          headers: {
            'x-user-id': authUser?.uid,
          },
        }
      )

      if (response.status === 200) {
        // await fetchResultsData({ refresh: false })
        window.location.reload()
      }
    } catch (error: any) {
      logger.error('Error updating product info:', error)
      setSubmitting(false)
      alert('Error updating product info')
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (data) {
      reset({
        title: data.title,
        price: data.pricing?.price,
        currency: data.pricing?.currency || 'USD',
        image: data.image,
        tags: data.tags?.join(', '),
      })
    }
  }, [data, reset])

  return (
    <ModalDialog
      width={600}
      trigger={({ openModal }) => (
        <Button variant='secondary' size='sm' onClick={openModal}>
          <PencilIcon height={16} />
        </Button>
      )}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
    >
      {() => (
        <form onSubmit={handleSubmit(onSubmit)} className='p-6'>
          <fieldset disabled={processing}>
            <TabGroup>
              <TabList className='mb-4 space-x-6'>
                <Tab
                  className={({ selected }) =>
                    clsx('border-b-2 text-lg font-bold', {
                      'border-b-black dark:border-b-white': selected,
                      'border-b-transparent': !selected,
                    })
                  }
                >
                  Edit Product
                </Tab>
              </TabList>
              <TabPanels className='min-h-[200px]'>
                <TabPanel className='flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4'>
                  <div className='min-w-[195px]'>
                    {image && (
                      <FadeImage
                        src={image}
                        alt=''
                        overlay
                        unoptimized
                        priority
                        rounded
                        width={200}
                        height={200}
                        className='aspect-square object-cover'
                      />
                    )}
                  </div>
                  <div className='flex flex-col space-y-2 md:w-4/5'>
                    <label className='block'>
                      <input
                        type='text'
                        placeholder='Title'
                        className='w-full rounded-md border px-2 py-2'
                        {...register('title')}
                      />
                    </label>
                    <div className='flex w-full items-center space-x-2'>
                      <label className='block flex-1'>
                        <input
                          type='text'
                          placeholder='Price'
                          className='font-cta w-full rounded-md border px-2 py-2'
                          {...register('price')}
                        />
                      </label>
                      <label className='block'>
                        <input
                          type='text'
                          placeholder='USD'
                          defaultValue={'USD'}
                          className='w-16 rounded-md border px-2 py-2'
                          {...register('currency')}
                        />
                      </label>
                    </div>
                    <label className='block'>
                      <div className='flex items-center overflow-hidden rounded-md border'>
                        <input
                          type='text'
                          placeholder='Image URL'
                          className='w-full px-2 py-2'
                          {...register('image')}
                        />
                        <button
                          type='button'
                          className='relative bg-gray-100 p-3'
                          disabled={processing}
                        >
                          {uploading ? (
                            <div className='animate-spin'>
                              <LoaderIcon height={18} />
                            </div>
                          ) : (
                            <PhotoIcon height={18} className='text-black/50' />
                          )}

                          <IKUpload
                            className='absolute inset-0 opacity-0'
                            folder='/dupe_com_temp/'
                            onUploadStart={() => setUploading(true)}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onError={(err: any) => {
                              logger.error(
                                'Error uploading product image to ImageKit',
                                {
                                  err,
                                }
                              )
                              setUploading(false)
                            }}
                            onSuccess={(res: UploadResponse) => {
                              const { filePath } = res
                              const ikPictureUrl = imageKit.url({
                                path: filePath,
                              })

                              setValue('image', ikPictureUrl)
                              setUploading(false)
                            }}
                          />
                        </button>
                      </div>
                    </label>
                    {/* <label className='block'>
                      <input
                        type='text'
                        placeholder='Tags (comma separated)'
                        className='w-full rounded-md border px-2 py-2'
                        {...register('tags')}
                      />
                    </label> */}
                  </div>
                </TabPanel>
              </TabPanels>
            </TabGroup>

            <div className='mt-4 flex items-center justify-between'>
              <div className='flex items-center space-x-2'>
                <TryAgain
                  retries={Infinity}
                  processing={refreshing}
                  refreshCallback={async () => {
                    const newData = await fetchResultsData({ refresh: true })
                    mutate(endpoint, newData, true)
                  }}
                />
              </div>
              <div className='flex items-center space-x-2'>
                <Button type='submit' size='md'>
                  Save
                </Button>
              </div>
            </div>
          </fieldset>
        </form>
      )}
    </ModalDialog>
  )
}

export default EditProductModal
